import { IText } from 'fabric';
import { getFontMetrics } from './font-metrics';

export interface HighlightRange {
  /** Inclusive char index in the text's `text`. */
  start: number;
  /** Exclusive char index. */
  end: number;
  color: string;
}

// Fabric Text class defaults (fabric/src/shapes/Text/Text.ts).
const FABRIC_FONT_SIZE_MULT = 1.13;
const FABRIC_FONT_SIZE_FRACTION = 0.222;

type CharBound = { left: number; width: number; kernedWidth?: number };

// Fabric internals — present at runtime but not in the public type defs.
type TextInternals = {
  __charBounds?: CharBound[][];
  _textLines?: string[][];
  _measureLine?: (i: number) => void;
  _fontSizeMult?: number;
  _fontSizeFraction?: number;
  _renderTextLinesBackground?: (ctx: CanvasRenderingContext2D) => void;
  getLineWidth?: (i: number) => number;
  get2DCursorLocation?: (i: number) => { lineIndex: number; charIndex: number };
};

// Patch IText so any instance with `highlightRanges` paints tight bands inside
// its own render pass. Textbox extends IText and inherits this automatically.
// Fabric hands us a transformed ctx, so we draw in text-local coords — no
// skew/scale math here.
const proto = IText.prototype as IText & TextInternals;
const originalRender = proto._renderTextLinesBackground;
proto._renderTextLinesBackground = function (this: IText, ctx: CanvasRenderingContext2D) {
  originalRender?.call(this, ctx);
  if (this.highlightRanges?.length) drawTightHighlights(ctx, this);
};

function drawTightHighlights(ctx: CanvasRenderingContext2D, text: IText): void {
  const t = text as IText & TextInternals;
  const lines = t._textLines;
  if (!lines) return;
  // Force charBounds populate if Fabric hasn't done it yet.
  if (!t.__charBounds?.length && t._measureLine) {
    for (let i = 0; i < lines.length; i++) t._measureLine(i);
  }
  const charBounds = t.__charBounds;
  if (!charBounds) return;

  const layout = readLayout(t);
  const textLen = (text.text ?? '').length;
  const w = text.width ?? 0;
  const h = text.height ?? 0;
  const ranges = text.highlightRanges ?? [];

  ctx.save();
  for (const range of ranges) {
    const rangeEnd = Math.min(range.end, textLen);
    const rangeStart = Math.min(range.start, rangeEnd);
    if (rangeStart >= rangeEnd) continue;

    ctx.fillStyle = range.color;

    for (const seg of splitRangeIntoLineSegments(t, rangeStart, rangeEnd)) {
      const lineBounds = charBounds[seg.lineIdx];
      const startBound = lineBounds?.[seg.startInLine];
      const endBound = lineBounds?.[seg.endInLine - 1];
      if (!startBound || !endBound) continue;

      const lineLeftOffset = getLineLeftOffset(t, seg.lineIdx);
      const localLeft = lineLeftOffset + startBound.left;
      const localRight = lineLeftOffset + endBound.left + (endBound.kernedWidth ?? endBound.width);
      const localLineBottom = seg.lineIdx * layout.interLineStep + layout.lineOwnHeight;
      const localBaseline = localLineBottom - layout.baselineFromLineBottom;
      const localTop = localBaseline - layout.capHeight;

      // ctx origin = bb center, so shift bb-space coords by (-w/2, -h/2).
      ctx.fillRect(localLeft - w / 2, localTop - h / 2, localRight - localLeft, layout.capHeight);
    }
  }
  ctx.restore();
}

type Layout = {
  lineOwnHeight: number; // per-line render height (fontSize × _fontSizeMult)
  interLineStep: number; // top-to-top distance between lines (× lineHeight)
  baselineFromLineBottom: number; // lineOwnHeight × _fontSizeFraction (the line's descent area)
  capHeight: number;
};

function readLayout(t: IText & TextInternals): Layout {
  const fontSize = t.fontSize ?? 16;
  const lineOwnHeight = fontSize * (t._fontSizeMult ?? FABRIC_FONT_SIZE_MULT);
  const metrics = getFontMetrics(t.fontFamily ?? 'Arial', fontSize, t.fontStyle ?? 'normal', t.fontWeight ?? 'normal');
  return {
    lineOwnHeight,
    interLineStep: lineOwnHeight * (t.lineHeight ?? 1),
    baselineFromLineBottom: lineOwnHeight * (t._fontSizeFraction ?? FABRIC_FONT_SIZE_FRACTION),
    capHeight: metrics.capHeight
  };
}

type LineSegment = { lineIdx: number; startInLine: number; endInLine: number };

/** Splits a char range into per-line segments using Fabric's own char→line mapping. */
function splitRangeIntoLineSegments(t: IText & TextInternals, start: number, end: number): LineSegment[] {
  const get = t.get2DCursorLocation;
  const lines = t._textLines ?? [];
  if (!get) return [];
  const first = get.call(t, start);
  const last = get.call(t, end - 1);
  const segments: LineSegment[] = [];
  for (let i = first.lineIndex; i <= last.lineIndex; i++) {
    segments.push({
      lineIdx: i,
      startInLine: i === first.lineIndex ? first.charIndex : 0,
      endInLine: i === last.lineIndex ? last.charIndex + 1 : lines[i].length
    });
  }
  return segments;
}

function getLineLeftOffset(t: IText & TextInternals, lineIdx: number): number {
  const lineWidth = t.getLineWidth?.(lineIdx) ?? 0;
  const w = t.width ?? 0;
  switch (t.textAlign ?? 'left') {
    case 'center':
      return (w - lineWidth) / 2;
    case 'right':
      return w - lineWidth;
    default:
      return 0;
  }
}
